import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FileText, X, Loader2, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ConsentService, LegalDocumentDTO } from '../services/apiService';

// Dopo il login controlla se ci sono documenti obbligatori non ancora accettati
// nella versione corrente; in quel caso mostra una modale bloccante con le sole
// scelte "Accetta e continua" o "Esci". La lettura richiede lo scroll completo.
export const ConsentGate: React.FC = () => {
    const { isAuthenticated, logout } = useAuth();

    const [pending, setPending] = useState<LegalDocumentDTO[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [checked, setChecked] = useState<Record<number, boolean>>({});
    const [openDocId, setOpenDocId] = useState<number | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const contentRef = useRef<HTMLDivElement | null>(null);
    const [reachedBottom, setReachedBottom] = useState(false);

    // Carica lo status ad ogni transizione ad autenticato.
    useEffect(() => {
        if (!isAuthenticated) { setPending([]); setLoaded(false); return; }
        let cancelled = false;
        ConsentService.getStatus()
            .then(s => { if (!cancelled) { setPending(s.pending || []); setLoaded(true); } })
            .catch(() => { if (!cancelled) setLoaded(true); });
        return () => { cancelled = true; };
    }, [isAuthenticated]);

    // Reset guardia scroll quando si apre un documento (abilita subito se corto).
    useEffect(() => {
        if (openDocId === null) return;
        setReachedBottom(false);
        requestAnimationFrame(() => {
            const el = contentRef.current;
            if (el && el.scrollHeight <= el.clientHeight + 4) setReachedBottom(true);
        });
    }, [openDocId]);

    const handleContentScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight <= 24) setReachedBottom(true);
    };

    const openDoc = useMemo(() => pending.find(d => d.id === openDocId) || null, [openDocId, pending]);
    const mandatoryPending = useMemo(() => pending.filter(d => d.mandatory), [pending]);
    const allMandatoryChecked = useMemo(
        () => mandatoryPending.every(d => checked[d.id]),
        [mandatoryPending, checked]
    );

    // Blocca solo se c'è almeno un documento obbligatorio da riaccettare.
    const mustGate = loaded && mandatoryPending.length > 0;
    if (!mustGate) return null;

    const handleAccept = async () => {
        setError('');
        if (!allMandatoryChecked) { setError('Devi accettare tutti i documenti obbligatori'); return; }
        const acceptances = pending
            .filter(d => checked[d.id])
            .map(d => ({ documentId: d.id, documentType: d.type, version: d.version }));
        setSubmitting(true);
        try {
            const status = await ConsentService.accept(acceptances);
            setPending(status.pending || []);
            setChecked({});
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Errore durante il salvataggio dei consensi. Riprova.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] bg-slate-900/70 flex items-center justify-center p-4 font-sans">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[92vh] flex flex-col border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shrink-0">
                            <ShieldCheck size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Aggiornamento condizioni d'uso</h2>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Sono state pubblicate nuove versioni dei documenti che devi accettare per continuare.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    <ul className="space-y-3">
                        {pending.map(doc => (
                            <li key={doc.id} className="flex items-start gap-3">
                                <input
                                    type="checkbox"
                                    required={doc.mandatory}
                                    checked={!!checked[doc.id]}
                                    readOnly
                                    aria-readonly="true"
                                    onClick={(e) => { e.preventDefault(); if (!checked[doc.id]) setOpenDocId(doc.id); }}
                                    className="mt-1 w-4 h-4 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                />
                                <span className="text-sm text-gray-700 flex-1 leading-relaxed">
                                    Ho letto e accetto{' '}
                                    <button
                                        type="button"
                                        onClick={() => setOpenDocId(doc.id)}
                                        className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-700 underline font-medium"
                                    >
                                        <FileText size={14} />
                                        {doc.title} (v{doc.version})
                                    </button>
                                    {doc.mandatory
                                        ? <span className="text-red-500 ml-1" aria-label="obbligatorio">*</span>
                                        : <span className="text-gray-400 ml-1 text-xs">(facoltativo)</span>}
                                    {doc.changelogNote && (
                                        <span className="block text-xs text-gray-500 mt-1">
                                            Cosa è cambiato: {doc.changelogNote}
                                        </span>
                                    )}
                                </span>
                            </li>
                        ))}
                    </ul>

                    {error && (
                        <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                            {error}
                        </div>
                    )}
                </div>

                <div className="p-4 border-t border-gray-200 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
                    <button
                        type="button"
                        onClick={() => logout()}
                        className="px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
                    >
                        Esci
                    </button>
                    <button
                        type="button"
                        disabled={submitting || !allMandatoryChecked}
                        onClick={handleAccept}
                        className="px-4 py-2 bg-indigo-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-bold hover:bg-indigo-700 flex items-center justify-center gap-2"
                    >
                        {submitting && <Loader2 className="animate-spin" size={18} />}
                        Accetta e continua
                    </button>
                </div>
            </div>

            {/* Modale lettura documento con scroll obbligatorio */}
            {openDoc && (
                <div className="fixed inset-0 z-[70] bg-black/60 flex items-stretch sm:items-center justify-center p-0 sm:p-6">
                    <div className="bg-white w-full sm:max-w-3xl sm:rounded-2xl shadow-2xl flex flex-col max-h-screen sm:max-h-[90vh]">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white sm:rounded-t-2xl">
                            <div>
                                <h2 className="text-lg font-bold text-gray-900">{openDoc.title}</h2>
                                <p className="text-xs text-gray-500 mt-0.5">Versione {openDoc.version}</p>
                            </div>
                            <button type="button" onClick={() => setOpenDocId(null)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500" aria-label="Chiudi">
                                <X size={20} />
                            </button>
                        </div>
                        <div
                            ref={contentRef}
                            onScroll={handleContentScroll}
                            className="p-6 overflow-y-auto flex-1 text-gray-800 text-sm leading-relaxed
                                [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-4 [&_h1]:text-gray-900
                                [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-6 [&_h2]:mb-2 [&_h2]:text-gray-900
                                [&_h3]:text-base [&_h3]:font-bold [&_h3]:mt-4 [&_h3]:mb-2 [&_h3]:text-gray-900
                                [&_p]:mb-3
                                [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3
                                [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-3
                                [&_li]:mb-1
                                [&_strong]:font-bold [&_em]:italic
                                [&_hr]:my-4 [&_hr]:border-gray-300
                                [&_a]:text-indigo-600 [&_a]:underline
                                [&_table]:w-full [&_table]:my-3 [&_table]:text-xs
                                [&_th]:font-bold [&_th]:border-b [&_th]:border-gray-300 [&_th]:p-2 [&_th]:text-left
                                [&_td]:border-b [&_td]:border-gray-200 [&_td]:p-2 [&_td]:align-top
                                [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-3"
                            dangerouslySetInnerHTML={{ __html: openDoc.contentHtml }}
                        />
                        <div className="p-4 border-t border-gray-200 flex flex-col-reverse sm:flex-row gap-3 sm:justify-between sm:items-center bg-white sm:rounded-b-2xl">
                            {!reachedBottom && (
                                <p className="text-xs text-amber-600 sm:mr-auto">
                                    Scorri il documento fino in fondo per poterlo accettare.
                                </p>
                            )}
                            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:ml-auto">
                                <button type="button" onClick={() => setOpenDocId(null)} className="px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50">
                                    Chiudi
                                </button>
                                <button
                                    type="button"
                                    disabled={!reachedBottom}
                                    onClick={() => { setChecked(prev => ({ ...prev, [openDoc.id]: true })); setOpenDocId(null); }}
                                    className="px-4 py-2 bg-indigo-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-bold hover:bg-indigo-700"
                                >
                                    Ho letto, accetto
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConsentGate;
